import { useEffect, useState } from "react";
import type { Deck } from "@deckloom/core/schema";

const loaded = new Set<string>();
let tick = 0;
export const FONTS_EVENT = "deckloom:fonts";

/** Re-renders (and lets layout effects re-measure) each time a batch of deck fonts finished loading. */
export function useFontsTick(): number {
  const [t, setT] = useState(tick);
  useEffect(() => {
    const h = () => setT(tick);
    window.addEventListener(FONTS_EVENT, h);
    return () => window.removeEventListener(FONTS_EVENT, h);
  }, []);
  return t;
}

/** Self-hosted families are bundled with the web app (Fontsource CSS imports), so they need no runtime loading. */
const SELF_HOSTED = new Set(["Carlito", "Caladea"]);

/**
 * The family text falls back to when its font is not on this machine and not served: Arial's metrics, which is what
 * PowerPoint substitutes for a font it does not have. Loaded with every deck that names such a font.
 */
export const FALLBACK_FAMILY = "Arimo";

/** CSS font-family for a run: its font (and east-asian font), then the fallback family, then the generic one. */
export function fontStack(font: string, eastAsian?: string): string {
  const names = [font];
  if (eastAsian && eastAsian !== font) names.push(eastAsian);
  if (!names.includes(FALLBACK_FAMILY)) names.push(FALLBACK_FAMILY);
  return `${names.map((n) => `"${n}"`).join(", ")}, sans-serif`;
}

/** Load every font the deck declares; resolves when the browser reports them ready (or on timeout). */
export async function loadDeckFonts(deck: Deck, timeoutMs = 4000): Promise<void> {
  const google: string[] = [];
  const families: string[] = [];
  const wanted = deck.fonts.filter((f) => f.load !== "none").map((f) => ({ family: f.resolved, load: f.load }));
  if (deck.fonts.some((f) => f.load === "none")) wanted.push({ family: FALLBACK_FAMILY, load: "google" });
  for (const f of wanted) {
    families.push(f.family);
    if (loaded.has(f.family)) continue;
    loaded.add(f.family);
    if (f.load === "google") google.push(f.family);
    else if (f.load === "self" && !SELF_HOSTED.has(f.family)) google.push(f.family);
  }
  const deadline = new Promise<void>((r) => setTimeout(r, timeoutMs));
  if (google.length) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${google.map((g) => `family=${encodeURIComponent(g)}:ital,wght@0,400;0,700;1,400;1,700`).join("&")}&display=swap`;
    const linkLoaded = new Promise<void>((r) => { link.onload = () => r(); link.onerror = () => r(); });
    document.head.appendChild(link);
    await Promise.race([linkLoaded, deadline]);
  }
  // Faces load lazily on first use; force the ones the deck needs so measurements (autofit, table rows) see final metrics.
  const loads = families.flatMap((f) => [`16px "${f}"`, `bold 16px "${f}"`]).map((spec) => document.fonts.load(spec).catch(() => undefined));
  await Promise.race([Promise.all([...loads, document.fonts.ready]), deadline]);
  tick++;
  window.dispatchEvent(new Event(FONTS_EVENT));
}
